import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { RouteList } from 'routeList';
import * as Yup from 'yup';
import { LanguageDropdown, Logo, EmailInput, PasswordInput, CheckboxInput } from '@btc-snxt/ui';
import { Api } from 'api';
import { RegisterRequest } from 'api/types/user/auth';
import { useAuth } from 'helpers/useAuth/useAuth';
import {
  StyledAuthBody,
  StyledAuthButton,
  StyledAuthHeader,
  StyledButtonWrapper,
  StyledCheckbox,
  StyledCheckboxBlock,
  StyledCheckboxText,
  StyledInput,
  StyledInputBlock,
  StyledLanguageSelect,
  StyledLink,
  StyledLogo,
  StyledSuggestion,
  StyledText,
  StyledTextBlock,
  StyledTextBody,
  StyledTextHeader,
  StyledTitle,
} from 'layouts/AuthenticationLayout/AuthenticationLayout.style';
import { StyledRegister } from './Register.style';

const Register: React.FC = () => {
  const { t } = useTranslation();

  const { login } = useAuth();

  const RegisterSchema = Yup.object().shape({
    email: Yup.string().email().required(),
    password: Yup.string().required(),
    password_confirmation: Yup.string().required(),
    tos: Yup.boolean().oneOf([true, false]).required(),
    marketing_consent: Yup.boolean().oneOf([true, false]),
    remember_me: Yup.boolean().oneOf([true, false]),
  });

  const [registerRequest] = useState({
    email: '',
    password: '',
    password_confirmation: '',
    tos: false,
    marketing_consent: false,
    remember_me: false,
  });

  const onSubmit = useCallback(
    async (request: RegisterRequest, helpers: FormikHelpers<RegisterRequest>) => {
      try {
        const response = await Api.user.auth.register(request);

        if (response.access_token) {
          await login(response.access_token, response.permissions);
        }
      } catch (e: any) {
        helpers.setErrors(e.response?.errors);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, t],
  );

  return (
    <>
      <StyledAuthHeader>
        <StyledLogo>
          <Link to={'/'}>
            <Logo />
          </Link>
        </StyledLogo>

        <StyledTitle>
          <StyledTextBlock>
            <StyledTextHeader>{t('auth.login.greeting')}</StyledTextHeader>

            <StyledTextBody>{t('auth.register.greeting_text')}</StyledTextBody>
          </StyledTextBlock>

          <StyledLanguageSelect>
            <LanguageDropdown />
          </StyledLanguageSelect>
        </StyledTitle>
      </StyledAuthHeader>

      <StyledAuthBody>
        <StyledRegister>
          <Formik
            initialValues={registerRequest}
            validationSchema={RegisterSchema}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, handleChange, values }) => (
              <Form onSubmit={handleSubmit}>
                <StyledInputBlock>
                  <StyledInput>
                    <StyledText>{t('auth.login.enter_email')}</StyledText>

                    <EmailInput
                      id={'email'}
                      name={'email'}
                      placeholder={t('label.email')}
                      onChange={handleChange}
                      value={values.email}
                    />
                  </StyledInput>

                  <StyledInput>
                    <StyledText>{t('auth.login.enter_password')}</StyledText>

                    <PasswordInput
                      id={'password'}
                      name={'password'}
                      placeholder={t('label.password')}
                      onChange={handleChange}
                      value={values.password}
                    />
                  </StyledInput>

                  <StyledInput>
                    <StyledText>{t('auth.register.password_confirm')}</StyledText>

                    <PasswordInput
                      id={'password_confirmation'}
                      name={'password_confirmation'}
                      placeholder={t('label.password')}
                      onChange={handleChange}
                      value={values.password_confirmation}
                    />
                  </StyledInput>
                </StyledInputBlock>

                <StyledCheckboxBlock>
                  <StyledCheckbox>
                    <Field as={CheckboxInput} name={'tos'} />

                    <StyledCheckboxText>{t('auth.register.tos')}</StyledCheckboxText>
                  </StyledCheckbox>

                  <StyledCheckbox>
                    <Field as={CheckboxInput} name={'marketing_consent'} />

                    <StyledCheckboxText>{t('auth.register.marketing_consent')}</StyledCheckboxText>
                  </StyledCheckbox>

                  <StyledCheckbox>
                    <Field as={CheckboxInput} name={'remember_me'} />

                    <StyledCheckboxText>{t('auth.login.remember_me')}</StyledCheckboxText>
                  </StyledCheckbox>
                </StyledCheckboxBlock>

                <StyledButtonWrapper>
                  <StyledAuthButton>{t('auth.login.sign_up')}</StyledAuthButton>
                </StyledButtonWrapper>

                <StyledSuggestion>
                  <StyledText>{t('auth.register.bottom_text')}</StyledText>

                  <StyledLink>
                    <NavLink to={RouteList.AUTHENTICATION.LOGIN.path}>
                      {t('auth.login.login')}
                    </NavLink>
                  </StyledLink>
                </StyledSuggestion>
              </Form>
            )}
          </Formik>
        </StyledRegister>
      </StyledAuthBody>
    </>
  );
};

export default Register;
